import {
  styled,
  Theme,
  CSSObject,
  Drawer as MuiDrawer,
  List,
  Divider,
  ListItem,
  ListItemButton as MuiListItemButton,
  ListItemIcon as MuiListItemIcon,
  ListItemText as MuiListItemText,
  FormControlLabel,
  Switch,
  Box,
  Badge,
} from '@mui/material'
import Icon from '@mdi/react'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { MenuItem } from '../constants'
import goToLocation from '../utils/goToLocation'
import { useEffect, useState } from 'react'
import { useCurrentPath } from '../hooks'
import { AudienceGroups } from '@npm-client-frontends/client-frontends-shared-types'

export const drawerWidth = 240

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
})

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
})

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}))

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  zIndex: theme.zIndex.drawer + 1,
  '& .MuiPaper-root': {
    backgroundColor: '#303c64',
    color: 'white',
  },
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}))

const ListItemButton = styled(MuiListItemButton)(() => ({
  minHeight: 48,
  '&:hover:not(.Mui-selected) .MuiTouchRipple-root': {
    backgroundColor: 'currentColor',
    opacity: 0.15,
  },
  '&.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: '#a2b7da',
    '& .MuiListItemText-primary': {
      color: '#384fa1',
    },
    '& .MuiListItemText-secondary': {
      color: '#000',
    },
    '& .MuiListItemIcon-root': {
      color: '#384fa1',
    },
  },
}))

const ListItemIcon = styled(MuiListItemIcon)(() => ({
  color: 'white',
  minWidth: 0,
  justifyContent: 'center',
}))

const ListItemText = styled(MuiListItemText)(() => ({
  '& .MuiListItemText-primary': {
    fontSize: 14,
  },
  '& .MuiListItemText-secondary': {
    fontSize: 12,
    color: '#a2b7da',
  },
}))

export default ({
  expanded,
  toggleExpanded,
  items,
  userAudienceGroups,
}: {
  expanded: boolean
  toggleExpanded: () => void
  items: {
    MenuItems: MenuItem[]
    ExternalMenuItems: MenuItem[]
  }
  userAudienceGroups: AudienceGroups[]
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const currentPath = useCurrentPath()

  const [isLoading, setIsLoading] = useState(true)
  const [isHover, setIsHover] = useState(false)
  const [filteredMenuItems, setFilteredMenuItems] = useState<MenuItem[]>([])
  const [filteredExternalMenuItems, setFilteredExternalMenuItems] = useState<
    MenuItem[]
  >([])

  useEffect(() => {
    if (userAudienceGroups.length > 0) {
      setIsLoading(false)
    }
  }, [userAudienceGroups])

  const isDrawerOpen = isMobile || isHover || expanded

  const { MenuItems, ExternalMenuItems } = items

  useEffect(() => {
    const enabledMenuItems = MenuItems.filter(
      ({ requiredGroups, rejectedGroups }) =>
        (requiredGroups.length === 0 ||
          requiredGroups.some((group) => userAudienceGroups.includes(group))) &&
        !rejectedGroups.some((group) => userAudienceGroups.includes(group)),
    )
    const enabledExternalMenuItems = ExternalMenuItems

    setFilteredMenuItems(enabledMenuItems)
    setFilteredExternalMenuItems(enabledExternalMenuItems)
  }, [userAudienceGroups])

  const handleClickLink = (link: string) => {
    goToLocation(link)
  }
  const handleClickExternalLink = (link: string) => {
    window.open(link, '_blank')
  }
  const handleMouseEnter = () => setIsHover(true)
  const handleMouseLeave = () => setIsHover(false)

  return (
    <Drawer
      variant="permanent"
      open={isDrawerOpen}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {!isLoading && (
        <>
          <DrawerHeader />

          <Box>
            <List>
              {filteredMenuItems.map(
                ({ label, icon, link, description, badge }) => {
                  return (
                    <ListItem
                      key={link}
                      disablePadding
                      sx={{ display: 'block' }}
                    >
                      <ListItemButton
                        sx={{
                          justifyContent: isDrawerOpen ? 'initial' : 'center',
                          px: 2.5,
                        }}
                        onClick={() => handleClickLink(link)}
                        selected={link.trim() === currentPath.trim()}
                      >
                        <ListItemIcon sx={{ mr: isDrawerOpen ? 2 : 'auto' }}>
                          <Icon path={icon} size={1} />
                        </ListItemIcon>
                        {badge ? (
                          <Badge
                            badgeContent={badge.label}
                            color={badge.color}
                            sx={{
                              display: isDrawerOpen ? 'block' : 'none',
                              '& .MuiBadge-badge': {
                                transform: 'scale(1) translate(100%, -40%)',
                                borderRadius: '6px',
                                px: 1,
                              },
                            }}
                          >
                            <ListItemText
                              primary={label}
                              secondary={description}
                              sx={{ opacity: isDrawerOpen ? 1 : 0 }}
                            />
                          </Badge>
                        ) : (
                          <ListItemText
                            primary={label}
                            secondary={description}
                            sx={{ opacity: isDrawerOpen ? 1 : 0 }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                  )
                },
              )}
            </List>
            <Divider sx={{ mx: 2, borderColor: 'hsla(0,0%,100%,0.28)' }} />
            <List sx={{ paddingBottom: 0 }}>
              {filteredExternalMenuItems.map(
                ({ label, icon, description, link }) => (
                  <ListItem
                    key={label}
                    disablePadding
                    sx={{ display: 'block' }}
                  >
                    <ListItemButton
                      sx={{
                        justifyContent: isDrawerOpen ? 'initial' : 'center',
                        px: 2.5,
                      }}
                    >
                      <ListItemIcon sx={{ mr: isDrawerOpen ? 2 : 'auto' }}>
                        <Icon path={icon} size={1} />
                      </ListItemIcon>
                      <ListItemText
                        primary={label}
                        secondary={description}
                        sx={{ opacity: isDrawerOpen ? 1 : 0 }}
                        onClick={() => handleClickExternalLink(link)}
                      />
                    </ListItemButton>
                  </ListItem>
                ),
              )}
            </List>
            {!isMobile && (
              <Box
                sx={{
                  visibility: isDrawerOpen ? 'visible' : 'hidden',
                  paddingLeft: '24px',
                  marginTop: '10px',
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      data-testid="sidebar-keep-open-switch"
                      checked={expanded}
                      onClick={toggleExpanded}
                    />
                  }
                  label="Keep open"
                />
              </Box>
            )}
          </Box>
        </>
      )}
    </Drawer>
  )
}
